import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { AppContext } from '../window';
import { NotificationContext } from './NotificationContext';
import { sendMessage, sendFlirt, sendGift } from '../api/chatApi';
import { upgradeAccount } from '../api/userApi';
import { removeDraftMessage } from '../utils/draftMessages';
import { safeLocalStorage } from '../utils/storage';

export enum PurchaseType {
  MESSAGE = 'message',
  FLIRT = 'flirt',
  GIFT = 'gift',
  PREMIUM = 'premium',
  MORE_MATCHES = 'more_matches'
}

/**
 * Utility function to get the current return route.
 * This captures the full URL path including pathname, search params, and hash.
 * Used to remember where to return after completing a purchase that required
 * navigating away (e.g., to /credits for payment).
 */
export const getCurrentReturnRoute = (): string => {
  return window.location.pathname + window.location.search + window.location.hash;
};

export interface PendingPurchase {
  id: string;
  type: PurchaseType;
  cost: number;
  data: any; // Type-specific data:
  // MESSAGE: { recipientId: number, conversationId: number, message: string, imageFile?: File, imagePreview?: string }
  // FLIRT: { recipientId: number }
  // GIFT: { recipientId: number, giftId: number, giftCredits: number }
  // PREMIUM: {} (no data needed)
  // MORE_MATCHES: {} (no data needed)
  notification: string;
  createdAt: number;
  returnRoute?: string; // Where to navigate after successful purchase
  targetUserNickname?: string;
  targetUserAvatar?: string;
}

interface PendingPurchaseContextType {
  pendingPurchase: PendingPurchase | null;
  setPendingPurchase: (purchase: Omit<PendingPurchase, 'id' | 'createdAt'> | null) => void;
  clearPendingPurchase: () => void;
  isInitialized: boolean; // Flag to indicate when localStorage has been checked
}

export const PendingPurchaseContext = createContext<PendingPurchaseContextType | undefined>(undefined);

const STORAGE_KEY = 'pendingPurchase';
const EXPIRATION_TIME = 60 * 60 * 1000; // 1 hour in milliseconds

export const PendingPurchaseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pendingPurchase, setPendingPurchase] = useState<PendingPurchase | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { user, costs, setUser } = useContext(AppContext);
  const { addNotification } = useContext(NotificationContext);

  // Helper function to check if purchase is expired
  const isPurchaseExpired = useCallback((purchase: PendingPurchase) => {
    return Date.now() - purchase.createdAt > EXPIRATION_TIME;
  }, []);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = safeLocalStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Check if the pending purchase has expired
        if (isPurchaseExpired(parsed)) {
          // Remove expired purchase
          safeLocalStorage.removeItem(STORAGE_KEY);
        } else {
          setPendingPurchase(parsed);
        }
      } catch (error) {
        console.error('Failed to parse pending purchase:', error);
      }
    }
    // Mark as initialized after checking localStorage
    setIsInitialized(true);
  }, [isPurchaseExpired]);

  // Save to localStorage whenever pendingPurchase changes
  useEffect(() => {
    if (pendingPurchase) {
      // Create a serializable version (File objects can't be stored in localStorage)
      const serializablePurchase = {
        ...pendingPurchase,
        data: {
          ...pendingPurchase.data,
          // Remove imageFile as it can't be serialized
          imageFile: undefined,
          // Keep imagePreview as it's a string
        }
      };
      safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(serializablePurchase));
    } else {
      safeLocalStorage.removeItem(STORAGE_KEY);
    }
  }, [pendingPurchase]);

  // Define all callbacks before they are used
  const setPendingPurchaseWrapper = useCallback((purchase: Omit<PendingPurchase, 'id' | 'createdAt'> | null) => {
    if (purchase === null) {
      setPendingPurchase(null);
    } else {
      const newPurchase: PendingPurchase = {
        ...purchase,
        id: Date.now().toString(),
        createdAt: Date.now()
      };
      setPendingPurchase(newPurchase);
    }
  }, []);

  const clearPendingPurchase = useCallback(() => {
    setPendingPurchase(null);
  }, []);

  // Credit deduction functions
  const deductMessageCredits = useCallback(() => {
    if (!user || !user.wallet) return;
    
    setUser(prevUser => {
      if (!prevUser || !prevUser.wallet) return prevUser;
      
      const newUser = { ...prevUser };
      const newWallet = { ...prevUser.wallet };
      
      // First use free messages if available
      if (newWallet.freeMessages > 0) {
        newWallet.freeMessages -= 1;
      } else {
        // Otherwise deduct from credits
        newWallet.credits = Math.max(0, newWallet.credits - costs.message);
      }
      
      newUser.wallet = newWallet;
      return newUser;
    });
  }, [costs.message, setUser]);

  const deductFlirtCredits = useCallback(() => {
    if (!user || !user.wallet) return;
    
    setUser(prevUser => {
      if (!prevUser || !prevUser.wallet) return prevUser;
      
      const newUser = { ...prevUser };
      const newWallet = { ...prevUser.wallet };
      
      // Deduct flirt cost from credits
      newWallet.credits = Math.max(0, newWallet.credits - costs.flirt);
      
      newUser.wallet = newWallet;
      return newUser;
    });
  }, [costs.flirt, setUser]);

  const deductGiftCredits = useCallback((giftCost: number) => {
    if (!user || !user.wallet) return;
    
    setUser(prevUser => {
      if (!prevUser || !prevUser.wallet) return prevUser;
      
      const newUser = { ...prevUser };
      const newWallet = { ...prevUser.wallet };
      
      // Deduct gift cost from credits
      newWallet.credits = Math.max(0, newWallet.credits - giftCost);
      
      newUser.wallet = newWallet;
      return newUser;
    });
  }, [setUser, user]);

  // Process functions need to be defined outside of useEffect for proper memoization
  const processSendMessage = useCallback(async (purchase: PendingPurchase) => {
    const { recipientId, conversationId, message, imageFile } = purchase.data;
    const formData = new FormData();
    formData.append('MessageContent[content]', message || '');
    
    // Add image file if present
    if (imageFile) {
      formData.append('_upload', imageFile);
    }
    
    const result = await sendMessage(recipientId, formData);
    
    if ('success' in result && result.success === false) {
      throw new Error(result.error);
    }
    
    // Remove draft message for this conversation since it was sent successfully
    if (conversationId) {
      removeDraftMessage(conversationId);
    }
    
    // Deduct credits after successful send
    deductMessageCredits();
  }, [deductMessageCredits]);

  const processSendFlirt = useCallback(async (purchase: PendingPurchase) => {
    const { recipientId } = purchase.data;
    const result = await sendFlirt(recipientId);
    
    if ('success' in result && result.success === false) {
      throw new Error(result.error);
    }
    
    // Deduct credits after successful send
    deductFlirtCredits();
  }, [deductFlirtCredits]);

  const processSendGift = useCallback(async (purchase: PendingPurchase) => {
    const { recipientId, giftId } = purchase.data;
    const result = await sendGift(recipientId, giftId);
    
    if ('success' in result && result.success === false) {
      throw new Error(result.error || 'Failed to send gift');
    }
    
    // Deduct credits after successful send
    deductGiftCredits(purchase.cost);
  }, [deductGiftCredits]);

  const processPremiumUpgrade = useCallback(async () => {
    const result = await upgradeAccount('premium');
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to upgrade to premium');
    }
  }, []);

  const processMoreMatches = useCallback(async () => {
    const result = await upgradeAccount('matches');
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to get more matches');
    }
  }, []);

  // Monitor credit balance changes and process pending purchase
  useEffect(() => {
    if (!user?.wallet?.credits || !pendingPurchase) return;

    const processPendingPurchase = async () => {
      // First check if purchase has expired
      if (isPurchaseExpired(pendingPurchase)) {
        clearPendingPurchase();
        return;
      }

      if (user.wallet.credits >= pendingPurchase.cost) {
        try {
          switch (pendingPurchase.type) {
            case PurchaseType.MESSAGE:
              await processSendMessage(pendingPurchase);
              break;
            case PurchaseType.FLIRT:
              await processSendFlirt(pendingPurchase);
              break;
            case PurchaseType.GIFT:
              await processSendGift(pendingPurchase);
              break;
            case PurchaseType.PREMIUM:
              await processPremiumUpgrade();
              break;
            case PurchaseType.MORE_MATCHES:
              await processMoreMatches();
              break;
          }
          
          addNotification({
            level: 'success',
            message: pendingPurchase.notification
          });
          clearPendingPurchase();
        } catch (error) {
          console.error('Failed to process pending purchase:', error);
        }
      }
    };

    processPendingPurchase();
  }, [user?.wallet?.credits, pendingPurchase, addNotification, clearPendingPurchase, processSendMessage, processSendFlirt, processSendGift, processPremiumUpgrade, processMoreMatches, isPurchaseExpired]);

  // Periodically check for expired purchases
  useEffect(() => {
    if (!pendingPurchase) return;

    const checkExpiration = () => {
      if (isPurchaseExpired(pendingPurchase)) {
        clearPendingPurchase();
      }
    };

    // Check every minute
    const intervalId = setInterval(checkExpiration, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [pendingPurchase, isPurchaseExpired, clearPendingPurchase]);

  // Clear in-memory state when a global logout event is dispatched
  useEffect(() => {
    const handleLogout = (_e: Event) => {
      clearPendingPurchase();
    };
    window.addEventListener('app:logout', handleLogout);
    return () => window.removeEventListener('app:logout', handleLogout);
  }, [clearPendingPurchase]);


  const contextValue = useMemo<PendingPurchaseContextType>(() => ({
    pendingPurchase,
    setPendingPurchase: setPendingPurchaseWrapper,
    clearPendingPurchase,
    isInitialized
  }), [pendingPurchase, setPendingPurchaseWrapper, clearPendingPurchase, isInitialized]);

  return (
    <PendingPurchaseContext.Provider value={contextValue}>
      {children}
    </PendingPurchaseContext.Provider>
  );
};

export const usePendingPurchase = () => {
  const context = useContext(PendingPurchaseContext);
  if (!context) {
    throw new Error('usePendingPurchase must be used within PendingPurchaseProvider');
  }
  return context;
};